import { HorizontalDotsChunky } from '@eventbrite/eds-iconography';
import { MoreActions } from '@eventbrite/eds-more-actions';
import { EVENT_INTERACTION, FormattedEvent } from '@eventbrite/event-renderer';
import { EventInteractions } from '@eventbrite/event-tracker';
import { gettext } from '@eventbrite/i18n';
import { useQueryClient } from '@tanstack/react-query';
import cloneDeep from 'lodash/cloneDeep';
import React, { FunctionComponent, useRef } from 'react';
import { connect } from 'react-redux';
import { SearchApiResponse } from '../../../../redux/api/search';
import { DiscoverState, EventSearch } from '../../../../types';
import { getEventSearchQueryKey } from '../../SearchPageData';
import './PromotedActionsMenuEventCard.scss';

export enum PromotedActions {
    hideAd = 'hide-ad',
}

export type HideThisAdEventCardButtonProps = {
    event: FormattedEvent;
    actionStyle?: string;
};

export type HideThisAdEventCardButtonReduxProps = {
    eventSearch: EventSearch;
};

function PromotedActionsMenuEventCardInternal({
    event,
    eventSearch,
    actionStyle = 'dark',
}: HideThisAdEventCardButtonProps & HideThisAdEventCardButtonReduxProps) {
    const buttonRef = useRef<HTMLSpanElement>(null);
    const { hideAd } = useHideAd(event.id, eventSearch);

    return (
        <span className="promoted-actions-menu-event-card" ref={buttonRef}>
            <MoreActions
                items={[
                    {
                        content: gettext('Hide this ad'),
                        value: PromotedActions.hideAd,
                    },
                ]}
                style={actionStyle}
                iconType={<HorizontalDotsChunky />}
                title={gettext('Promoted event actions')}
                onAction={() => {
                    hideAd();
                    sendActionMetrics(
                        event,
                        EventInteractions.HideAd,
                        buttonRef,
                    );
                }}
            />
        </span>
    );
}

export function useHideAd(eventId: string, eventSearch: EventSearch) {
    const client = useQueryClient();

    return {
        hideAd: () => {
            client.setQueryData<SearchApiResponse>(
                getEventSearchQueryKey(eventSearch),
                (apiResponse) => {
                    if (!apiResponse) {
                        return apiResponse;
                    }

                    const promotedEvents =
                        apiResponse.data.events.promoted_results;

                    const newApiResponse = cloneDeep(apiResponse);
                    newApiResponse.data.events.promoted_results =
                        promotedEvents.filter(({ id }) => eventId !== id);

                    return newApiResponse;
                },
            );
        },
    };
}

function sendActionMetrics(
    event: FormattedEvent,
    action: EventInteractions,
    element: React.RefObject<HTMLSpanElement>,
) {
    const interactionEvent = new CustomEvent(EVENT_INTERACTION, {
        detail: {
            id: event.id,
            action,
            event,
        },
        bubbles: true,
    });

    element.current?.dispatchEvent(interactionEvent);
}

const _mapStateToProps = (state: DiscoverState) => ({
    eventSearch: state.search.eventSearch,
});

export const PromotedActionsMenuEventCard: FunctionComponent<HideThisAdEventCardButtonProps> =
    connect(_mapStateToProps)(PromotedActionsMenuEventCardInternal);
